import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, LogOut, TrendingUp, Plus, ArrowRight, AlertCircle } from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import toast from 'react-hot-toast';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    dashboardAPI.get()
      .then(res => setData(res.data.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <span className="spinner" style={{ width:32, height:32, borderWidth:3 }} />
    </div>
  );

  const stats = data?.stats || {};
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('dashboard.greeting.morning') : hour < 18 ? t('dashboard.greeting.afternoon') : t('dashboard.greeting.evening');

  return (
    <div>
      <div style={{ marginBottom:32 }}>
        <h1 style={{ fontSize:28, fontWeight:800, marginBottom:4 }}>{greeting}, {user?.full_name?.split(' ')[0]} 👋</h1>
        <p style={{ color:'var(--text-secondary)', fontSize:14 }}>{t('dashboard.subtitle')}</p>
      </div>

      <div className="grid-4 stats-grid" style={{ marginBottom:28 }}>
        {[
          { label: t('dashboard.stat.properties'), value: stats.total_properties || 0, icon: Building2, color:'var(--accent)' },
          { label: t('dashboard.stat.guests'), value: stats.current_guests || 0, icon: Users, color:'var(--success)' },
          { label: t('dashboard.stat.checkouts'), value: stats.today_checkouts || 0, icon: LogOut, color:'var(--warning)' },
          { label: t('dashboard.stat.income'), value: `${(stats.income_this_month || 0).toLocaleString()} MAD`, icon: TrendingUp, color:'var(--gold)' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="stat-card">
            <div style={{ width:40, height:40, borderRadius:10, background:`${color}20`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12 }}>
              <Icon size={18} color={color} />
            </div>
            <div className="stat-number" style={{ color, fontSize:26 }}>{value}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <h3 style={{ fontSize:16, fontWeight:700 }}>{t('dashboard.checkoutsToday')}</h3>
            {stats.today_checkouts > 0 && <span className="badge badge-warning">{stats.today_checkouts} {t('table.guests')}</span>}
          </div>
          {data?.today_checkouts?.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'var(--text-muted)', fontSize:14 }}>{t('dashboard.noCheckoutsToday')}</div>
          ) : data?.today_checkouts?.map(b => (
            <div key={b.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight:500, fontSize:14 }}>{b.guest_name}</div>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{b.property_name}</div>
              </div>
              <span className="badge badge-warning">{t('dashboard.today')}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <h3 style={{ fontSize:16, fontWeight:700 }}>{t('dashboard.checkoutsTomorrow')}</h3>
            {stats.tomorrow_checkouts > 0 && <span className="badge badge-accent">{stats.tomorrow_checkouts}</span>}
          </div>
          {data?.tomorrow_checkouts?.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'var(--text-muted)', fontSize:14 }}>{t('dashboard.noCheckoutsTomorrow')}</div>
          ) : data?.tomorrow_checkouts?.map(b => (
            <div key={b.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight:500, fontSize:14 }}>{b.guest_name}</div>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{b.property_name}</div>
              </div>
              <span className="badge badge-accent">{t('dashboard.tomorrow')}</span>
            </div>
          ))}
        </div>

        <div className="card" style={{ gridColumn:'1/-1' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <h3 style={{ fontSize:16, fontWeight:700 }}>{t('dashboard.recentBookings')}</h3>
            <Link to="/bookings" className="btn btn-ghost" style={{ padding:'6px 14px', fontSize:13 }}>{t('dashboard.viewAll')} <ArrowRight size={14} /></Link>
          </div>
          {data?.recent_bookings?.length === 0 ? (
            <div className="empty-state">
              <AlertCircle size={40} />
              <h3>{t('dashboard.noBookings')}</h3>
              <p>{t('dashboard.noBookingsDesc')}</p>
              <Link to="/properties" className="btn btn-primary"><Plus size={16} /> {t('action.addProperty')}</Link>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('table.guest')}</th><th>{t('table.property')}</th><th>{t('table.checkIn')}</th><th>{t('table.checkOut')}</th><th>{t('table.source')}</th><th>{t('table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {data?.recent_bookings?.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontWeight:500 }}>{b.guest_name}</td>
                    <td style={{ color:'var(--text-secondary)' }}>{b.property_name}</td>
                    <td style={{ color:'var(--text-secondary)' }}>{new Date(b.check_in).toLocaleDateString()}</td>
                    <td style={{ color:'var(--text-secondary)' }}>{new Date(b.check_out).toLocaleDateString()}</td>
                    <td><span className="badge badge-accent">{t('sources.' + b.source) || b.source}</span></td>
                    <td><span className={`badge badge-${b.status==='confirmed'?'success':b.status==='cancelled'?'danger':'warning'}`}>{t('status.' + b.status)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
